import {
  useTriggerDataScienceDeploymentMutation,
  useUpdateDataScienceTaskMutation
} from 'app/apis/commandApi';
import {
  useGetDataScienceTaskStagingDataQuery,
  useGetDataScienceTasksQuery
} from 'app/apis/queryApi';
import { RootState } from 'app/store';
import AnchorButton from 'common/components/buttons/anchor-button';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import Spinner from 'common/components/loading/spinner';
import { createUpdateCacheKey } from 'features/data-science/task-cache-keys';
import { DataScienceFeatures } from 'features/data-science/task-features';
import { DataScienceTaskStatus } from 'features/data-science/tasks-status';
import { DataScienceTask } from 'features/data-science/tasks.models';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { onError, onSuccess } from 'features/toast/toast.slice';
import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

export const SettingsDataScienceTasksReviewPage: FC = () => {
  const { siteId } = useSelector((state: RootState) => state.selectedSite);

  const { targetDataScienceFeature } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    data: dataScienceTaskStagingData,
    isFetching,
    refetch
  } = useGetDataScienceTaskStagingDataQuery(
    {
      siteId,
      dataScienceFeature: targetDataScienceFeature as DataScienceFeatures
    },
    {
      skip: targetDataScienceFeature == null
    }
  );

  const {
    data: dataScienceTasksData,
    isFetching: isFetchingDataScienceTasks,
    refetch: refetchDataScienceTasks
  } = useGetDataScienceTasksQuery({
    siteId
  });

  const dataScienceTask = useMemo(() => {
    if (!isFetchingDataScienceTasks && dataScienceTasksData) {
      const dataScienceTask = dataScienceTasksData.find(
        (task) => task.machineLearningModel === targetDataScienceFeature
      );

      if (dataScienceTask) {
        return dataScienceTask;
      }
    }
    return {} as DataScienceTask;
  }, [
    dataScienceTasksData,
    isFetchingDataScienceTasks,
    targetDataScienceFeature
  ]);

  const [triggerDeploy, { isLoading: isLoadingDeploy }] =
    useTriggerDataScienceDeploymentMutation();

  const handleDeploy = async () => {
    if (targetDataScienceFeature) {
      try {
        await triggerDeploy({
          siteId,
          machineLearningModel: targetDataScienceFeature
        }).unwrap();
        dispatch(
          onSuccess({
            title: 'Deployment triggered successfully.',
            description: 'The deployment has been triggered successfully.'
          })
        );
      } catch (error) {
        dispatch(
          onError({
            title: 'Failed to trigger deployment.',
            description: 'Something went wrong, see logs for more details.'
          })
        );
      }
    }
  };

  const [updateDataScienceTaskMutation] = useUpdateDataScienceTaskMutation({
    fixedCacheKey: createUpdateCacheKey(
      targetDataScienceFeature as DataScienceFeatures,
      siteId
    )
  });

  const scheduleRetraining = async () => {
    const enabledDataScienceTask = {
      siteId,
      dataScienceTask: {
        ...dataScienceTask,
        status: DataScienceTaskStatus.SCHEDULED
      }
    };

    void updateDataScienceTaskMutation(enabledDataScienceTask)
      .then(() =>
        dispatch(
          onSuccess({
            title: 'Data Science Task Scheduled',
            description: `The Data Science Task '${dataScienceTask.machineLearningModel}' has been scheduled successfully.`
          })
        )
      )
      .catch(() => {
        dispatch(
          onError({
            title: 'Failed to schedule Data Science Task',
            description: 'Something went wrong, see logs for more details.'
          })
        );
      });
  };

  const refreshStaging = () => {
    void refetch();
  };

  const navigateBack = () => {
    navigate(`/settings/data-science/tasks`);
  };

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
        <section className="mx-6 my-6">
          <div className="flex items-center justify-between border-b border-neutral-200 pb-4 mb-4 mt-2">
            <span className="flex items-center">
              <h2 className="text-primary-700 text-l">
                Data Science Staging Data
              </h2>
              <button onClick={refreshStaging}>
                <i className="ra-icons-generic-forward text-neutral-500 hover:text-primary-50 hover:border-primary-50 transition-all flex items-center w-fit text-sm ml-2"></i>
              </button>
            </span>
          </div>
          {isFetching ? (
            <div className="flex justify-center mt-4">
              <Spinner />
            </div>
          ) : (
            <>
              {dataScienceTaskStagingData &&
              dataScienceTaskStagingData.length === 0 ? (
                <p className="mt-4">No staging data available.</p>
              ) : (
                <div className="mt-4 w-full max-w-6xl">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {dataScienceTaskStagingData?.map((data, index) => (
                      <div
                        key={index}
                        className="border border-neutral-200 rounded p-4"
                      >
                        <p className="my-2 font-medium break-words text-primary-700">
                          {data.fileName}
                        </p>
                        <p className="my-2 text-sm text-primary-700">
                          {data.fileSize} bytes
                        </p>
                        <AnchorButton
                          theme={ButtonTheme.CREATE}
                          href={data.signedUrl}
                        >
                          <p>Download</p>
                        </AnchorButton>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="flex justify-start gap-3 mt-4">
            <Button
              onClick={async () => await handleDeploy()}
              theme={ButtonTheme.PRIMARY}
              identifier="refresh-staging-data"
              isLoading={isLoadingDeploy}
            >
              <p>Approve Deploy</p>
            </Button>
            <Button
              onClick={async () => await scheduleRetraining()}
              theme={ButtonTheme.SECONDARY}
              identifier="schedule-retraining"
            >
              <p>Schedule Re-Training</p>
            </Button>
            <Button
              onClick={() => navigateBack()}
              theme={ButtonTheme.ERROR}
              identifier="back-to-tasks"
              isLoading={isLoadingDeploy}
            >
              <p>Back</p>
            </Button>
          </div>
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};

export default SettingsDataScienceTasksReviewPage;
